import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Breadcrumb.css';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

// Route label mapping
const ROUTE_LABELS: Record<string, string> = {
  '': 'Dashboard',
  'requests': 'Requests',
  'missions': 'Missions',
  'map': 'Map',
  'my-patients': 'My Patients — Treatment',
  'my-transport-patients': 'My Patients — Transport',
  'statistics': 'Statistics',
  'capacity-planning': 'Capacity Planning',
  'flow': 'Flow Diagram',
  'assets': 'Assets',
  'startpoints': 'Hubs',
  'hospital': 'Treatment Facilities',
  'patient-import': 'Patient Import',
  'asset-templates': 'Asset Templates',
  'day-templates': 'Day Templates',
  'announcement-ticker': 'Announcements',
  'users': 'Users',
  'settings': 'Settings',
  'audit-logs': 'Audit Logs',
  'exercise-control': 'Exercise Control',
  'data-sync': 'Data Sync',
  'documentation': 'Documentation',
  'support': 'Support',
  'about': 'About',
  'notifications': 'Notifications',
  'notification-settings': 'Notification Settings',
  'change-password': 'Change Password',
  'schedules': 'Schedules',
  'transport-planning': 'Transport Planning',
  'transport-booking': 'Transport Booking',
  'manage-offers': 'Manage Offers',
  'offers': 'Offers',
  'new': 'New Request',
  'edit': 'Edit',
  'create': 'Create Offer',
  'extend-transport': 'Extend Transport',
};

interface BreadcrumbProps {
  /** Override items instead of auto-generating from URL */
  items?: BreadcrumbItem[];
  /** Additional label for the current item (e.g., request ID) */
  currentLabel?: string;
}

function Breadcrumb({ items, currentLabel }: BreadcrumbProps) {
  const location = useLocation();

  const breadcrumbs: BreadcrumbItem[] = items || (() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const crumbs: BreadcrumbItem[] = [{ label: 'Dashboard', path: '/' }];

    let currentPath = '';
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      currentPath += `/${segment}`;

      const label = ROUTE_LABELS[segment];
      const isLast = i === segments.length - 1;

      if (label) {
        crumbs.push({
          label: isLast && currentLabel ? currentLabel : label,
          path: isLast ? undefined : currentPath,
        });
      } else if (isLast && currentLabel) {
        // Dynamic segment with label override (e.g., request ID)
        crumbs.push({ label: currentLabel });
      } else if (segment.match(/^[A-Z]{2,4}-\d{4}-\d+$/)) {
        // Looks like a request/mission ID
        crumbs.push({ label: segment, path: isLast ? undefined : currentPath });
      } else {
        // UUID or unknown — use parent label context
        crumbs.push({
          label: currentLabel || 'Details',
          path: isLast ? undefined : currentPath,
        });
      }
    }

    return crumbs;
  })();

  if (breadcrumbs.length <= 1) return null;

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol className="breadcrumb-list">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <li key={index} className={`breadcrumb-item ${isLast ? 'breadcrumb-current' : ''}`}>
              {!isLast && item.path ? (
                <>
                  <Link to={item.path} className="breadcrumb-link">{item.label}</Link>
                  <span className="breadcrumb-separator" aria-hidden="true">/</span>
                </>
              ) : (
                <span className="breadcrumb-text">{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
